import { useState } from 'react';
import { useTranslation, TFunction } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme';

interface FAQItem {
  question: string;
  answer: string;
  icon: string;
}

interface Category {
  id: string;
  label: string;
  icon: string;
  color: string;
  isNew?: boolean;
  faqs: FAQItem[];
}

function getCategories(t: TFunction): Category[] {
  return [
    {
      id: 'sistema',
      label: t('faq_cat_sistema'),
      icon: 'ri-cpu-line',
      color: '#00cfff',
      faqs: [
        { icon: 'ri-layout-grid-line', question: t('faq_s1_q'), answer: t('faq_s1_a') },
        { icon: 'ri-wifi-off-line',    question: t('faq_s2_q'), answer: t('faq_s2_a') },
        { icon: 'ri-device-line',      question: t('faq_s3_q'), answer: t('faq_s3_a') },
      ],
    },
    {
      id: 'implementacion',
      label: t('faq_cat_implementacion'),
      icon: 'ri-rocket-2-line',
      color: '#00ff9d',
      faqs: [
        { icon: 'ri-timer-flash-line', question: t('faq_i1_q'), answer: t('faq_i1_a') },
        { icon: 'ri-map-pin-2-line',   question: t('faq_i2_q'), answer: t('faq_i2_a') },
      ],
    },
    {
      id: 'facturacion',
      label: t('faq_cat_facturacion'),
      icon: 'ri-file-shield-2-line',
      color: '#facc15',
      faqs: [
        { icon: 'ri-government-line',    question: t('faq_f1_q'), answer: t('faq_f1_a') },
        { icon: 'ri-receipt-line',       question: t('faq_f2_q'), answer: t('faq_f2_a') },
        { icon: 'ri-shield-star-line',   question: t('faq_f3_q'), answer: t('faq_f3_a') },
        { icon: 'ri-global-line',        question: t('faq_f4_q'), answer: t('faq_f4_a') },
        { icon: 'ri-add-circle-line',    question: t('faq_f5_q'), answer: t('faq_f5_a') },
        { icon: 'ri-shopping-cart-line', question: t('faq_f6_q'), answer: t('faq_f6_a') },
      ],
    },
    {
      id: 'republicafel',
      label: t('faq_cat_republicafel'),
      icon: 'ri-building-4-line',
      color: '#f97316',
      isNew: true,
      faqs: [
        { icon: 'ri-information-line',        question: t('faq_rfel1_q'), answer: t('faq_rfel1_a') },
        { icon: 'ri-user-settings-line',      question: t('faq_rfel2_q'), answer: t('faq_rfel2_a') },
        { icon: 'ri-link-m',                  question: t('faq_rfel3_q'), answer: t('faq_rfel3_a') },
        { icon: 'ri-dashboard-3-line',        question: t('faq_rfel4_q'), answer: t('faq_rfel4_a') },
        { icon: 'ri-money-dollar-circle-line',question: t('faq_rfel5_q'), answer: t('faq_rfel5_a') },
        { icon: 'ri-whatsapp-line',           question: t('faq_rfel6_q'), answer: t('faq_rfel6_a') },
        { icon: 'ri-calendar-check-line',     question: t('faq_rfel7_q'), answer: t('faq_rfel7_a') },
      ],
    },
    {
      id: 'digital_dev',
      label: t('faq_cat_digital_dev'),
      icon: 'ri-smartphone-line',
      color: '#a78bfa',
      isNew: true,
      faqs: [
        { icon: 'ri-time-line',         question: t('faq_dd1_q'), answer: t('faq_dd1_a') },
        { icon: 'ri-price-tag-3-line',  question: t('faq_dd2_q'), answer: t('faq_dd2_a') },
        { icon: 'ri-code-s-slash-line', question: t('faq_dd3_q'), answer: t('faq_dd3_a') },
        { icon: 'ri-tools-line',        question: t('faq_dd4_q'), answer: t('faq_dd4_a') },
        { icon: 'ri-server-line',       question: t('faq_dd5_q'), answer: t('faq_dd5_a') },
      ],
    },
    {
      id: 'desarrollo',
      label: t('faq_cat_desarrollo'),
      icon: 'ri-code-s-slash-line',
      color: '#00cfff',
      faqs: [
        { icon: 'ri-settings-4-line',     question: t('faq_d1_q'), answer: t('faq_d1_a') },
        { icon: 'ri-expand-up-down-line', question: t('faq_d2_q'), answer: t('faq_d2_a') },
      ],
    },
    {
      id: 'planes',
      label: t('faq_cat_planes'),
      icon: 'ri-price-tag-3-line',
      color: '#00ff9d',
      faqs: [
        { icon: 'ri-stack-line',              question: t('faq_pl1_q'), answer: t('faq_pl1_a') },
        { icon: 'ri-refresh-line',            question: t('faq_pl2_q'), answer: t('faq_pl2_a') },
        { icon: 'ri-arrow-up-circle-line',    question: t('faq_pl3_q'), answer: t('faq_pl3_a') },
        { icon: 'ri-add-box-line',            question: t('faq_pl4_q'), answer: t('faq_pl4_a') },
        { icon: 'ri-robot-2-line',            question: t('faq_pl5_q'), answer: t('faq_pl5_a') },
        { icon: 'ri-customer-service-2-line', question: t('faq_pl6_q'), answer: t('faq_pl6_a') },
        { icon: 'ri-eye-line',                question: t('faq_pl7_q'), answer: t('faq_pl7_a') },
      ],
    },
  ];
}

interface QuestionCardProps {
  faq: FAQItem;
  color: string;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
  isLight: boolean;
}

function QuestionCard({ faq, color, isOpen, onToggle, index, isLight }: QuestionCardProps) {
  return (
    <div
      className="rounded-2xl border transition-all duration-300 overflow-hidden cursor-pointer"
      style={{
        borderColor: isOpen
          ? color + '50'
          : isLight ? 'rgba(10,22,40,0.10)' : 'rgba(255,255,255,0.07)',
        background: isOpen
          ? isLight
            ? `linear-gradient(135deg, ${color}12 0%, rgba(248,251,255,1) 70%)`
            : `linear-gradient(135deg, ${color}0a 0%, rgba(10,20,40,0.97) 70%)`
          : isLight ? '#ffffff' : 'rgba(10,20,40,0.6)',
        animationDelay: `${index * 60}ms`,
        boxShadow: isLight && isOpen ? `0 2px 12px ${color}15` : 'none',
      }}
      onClick={onToggle}
    >
      <div className="flex items-start gap-4 p-5">
        {/* Icon */}
        <div
          className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-300"
          style={{
            backgroundColor: isOpen
              ? color + '22'
              : isLight ? 'rgba(10,22,40,0.06)' : 'rgba(255,255,255,0.05)',
            color: isOpen
              ? color
              : isLight ? 'rgba(10,22,40,0.35)' : 'rgba(255,255,255,0.35)',
          }}
        >
          <i className={`${faq.icon} text-base`}></i>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <h4
              className="font-heading text-sm font-bold leading-snug transition-colors duration-200"
              style={{
                color: isOpen
                  ? isLight ? '#0a1628' : '#fff'
                  : isLight ? 'rgba(10,22,40,0.82)' : 'rgba(255,255,255,0.80)',
              }}
            >
              {faq.question}
            </h4>
            <div
              className="w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300"
              style={{
                backgroundColor: isOpen
                  ? color + '22'
                  : isLight ? 'rgba(10,22,40,0.06)' : 'rgba(255,255,255,0.06)',
                color: isOpen
                  ? color
                  : isLight ? 'rgba(10,22,40,0.35)' : 'rgba(255,255,255,0.35)',
                transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
              }}
            >
              <i className="ri-add-line text-xs font-bold"></i>
            </div>
          </div>

          {/* Answer */}
          <div
            className="overflow-hidden transition-all duration-350"
            style={{
              maxHeight: isOpen ? '420px' : '0px',
              opacity: isOpen ? 1 : 0,
              marginTop: isOpen ? '12px' : '0px',
              transition: 'max-height 0.35s ease, opacity 0.25s ease, margin-top 0.25s ease',
            }}
          >
            <div
              className="w-full h-px mb-3"
              style={{ backgroundColor: color + '20' }}
            />
            <p
              className="font-body text-xs leading-relaxed"
              style={{ color: isLight ? 'rgba(10,22,40,0.62)' : 'rgba(255,255,255,0.60)' }}
            >
              {faq.answer}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const color = '#00cfff';
  const allFaqs = getCategories(t).flatMap((c) => c.faqs);

  const toggleQuestion = (q: string) => {
    setOpenQuestion(openQuestion === q ? null : q);
  };

  return (
    <section id="faq" className="bg-navy-950 py-12 md:py-24 px-4 sm:px-5 md:px-6 relative overflow-hidden">

      {/* Decorative bg glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(ellipse, ${color}06 0%, transparent 70%)`,
          filter: 'blur(60px)',
        }}
      />

      <div className="max-w-5xl mx-auto relative z-10">

        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <div
            className="font-heading font-black leading-none select-none pointer-events-none mb-1"
            style={{
              fontSize: 'clamp(48px, 12vw, 148px)',
              background: 'linear-gradient(90deg, #00cfff22, #00ff9d22)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '0.08em',
            }}
          >
            FAQ
          </div>

          <span className="inline-block text-electric text-xs md:text-sm font-semibold tracking-widest uppercase font-body mb-2 md:mb-3">
            {t('faq_label')}
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-3 leading-tight">
            {t('faq_title_1')}{' '}
            <span style={{ background: 'linear-gradient(90deg, #00cfff, #00ff9d)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {t('faq_title_2')}
            </span>
          </h2>
          <p
            className="font-body text-xs md:text-sm max-w-lg mx-auto"
            style={{ color: isLight ? 'rgba(10,22,40,0.50)' : 'rgba(255,255,255,0.50)' }}
          >
            {t('faq_cat_sub')}
          </p>
        </div>

        {/* Questions Grid (all questions, no category tabs) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {allFaqs.map((faq, i) => (
            <QuestionCard
              key={faq.question}
              faq={faq}
              color={color}
              isOpen={openQuestion === faq.question}
              onToggle={() => toggleQuestion(faq.question)}
              index={i}
              isLight={isLight}
            />
          ))}
        </div>

      </div>
    </section>
  );
}
